import { useCallback, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import * as Sentry from '@sentry/react'

import { Button, Col, Container, Row } from 'react-bootstrap'
import {
  ROUTE_LOGIN,
  ROUTE_TODAY,
  ROUTE_TOS_ACCEPTANCE
} from 'components/common/helpers/Paths'
import Loading from 'templates/Loading'
import {
  completeAppleLoginWithCodeAndState,
  completeGoogleLoginWithCodeAndState,
  completeOpenIDLoginWithCodeAndState,
  login,
  REMBLE_OAUTH_DOMAIN,
  REMBLE_OAUTH_ID_TOKEN_KEY,
  REMBLE_OAUTH_STATE_KEY,
  setLocalAuthKey
} from '.'
import {
  EVENT_ACTION_LOGIN,
  EVENT_CATEGORY_ACCOUNT,
  saveUserEvent
} from 'components/common/api/UserEvents'
import { navigateTo } from 'components/common/navigation'
import { postMobileToken } from 'components/common/helpers/Mobile'
import { getValueForKey } from 'components/common/helpers/Storage'

/**
 * OAuth login component, used for OAuth login flows
 */
export default function OAuthLogin({ setIsAuthenticated, history }) {
  const { search } = useLocation()
  const [isLoading, setIsLoading] = useState(true)
  const [signupError, setSignupError] = useState('')

  // Did we redirect with a return path from an auth-required page?
  const searchParams = new URLSearchParams(search)
  const code = searchParams.get('code')
  const provider = searchParams.get('provider')
  const state = searchParams.get('state')

  /**
   * Helper function to handle successful authentication
   * @param {Object} data JWT Authentication data
   * @param {String} redirectURL The URL to redirect users to
   */
  const handleAuthSuccess = useCallback(
    async (data, redirectURL) => {
      login(data)
      if (setIsAuthenticated) setIsAuthenticated(true)

      // For mobile, we need to post the token to the keychain
      postMobileToken(data.token)
      setLocalAuthKey(data.token) // Sets local storage to dismiss the in-app auth screen

      // Log access
      await saveUserEvent(
        EVENT_CATEGORY_ACCOUNT,
        EVENT_ACTION_LOGIN,
        'oauth',
        provider
      )

      // Clear the state
      localStorage.removeItem(REMBLE_OAUTH_STATE_KEY)

      return navigateTo(redirectURL, history)
    },
    [history, setIsAuthenticated, provider]
  )

  useEffect(() => {
    // Get the id token from local storage
    const idToken = localStorage.getItem(REMBLE_OAUTH_ID_TOKEN_KEY)
    const domain = getValueForKey(REMBLE_OAUTH_DOMAIN)

    // If we have an id token, we're already logged in, so we can skip the state check
    if (!idToken) {
      // If we don't have a sate, code or a provider, we can't log in
      if (!state || !code || !provider) {
        setSignupError('No state, code or provider provided. Unable to log in.')
        setIsLoading(false)
        return
      }

      // Let's compare the state we got with the state we sent
      const stateFromStorage = getValueForKey(REMBLE_OAUTH_STATE_KEY)

      // Don't use explicit string comparison
      // eslint-disable-next-line eqeqeq
      if (stateFromStorage != state) {
        setSignupError(
          `State mismatch: ${state} does not match ${stateFromStorage}. Unable to log in.`
        )
        setIsLoading(false)
        return
      }
    }

    try {
      // Use the appropriate provider to get the user's info
      const providers = {
        apple: completeAppleLoginWithCodeAndState,
        google: completeGoogleLoginWithCodeAndState,
        oauth: completeOpenIDLoginWithCodeAndState
      }
      const loginFunction = providers[provider]

      // If we don't have a login function, we can't log in,
      // so just redirect to the login page
      if (!loginFunction) {
        window.location.href = ROUTE_LOGIN
        return
      }

      loginFunction(code, state, idToken, domain)
        .then(result => {
          // Did we get an error?
          if (result.detail) {
            setSignupError(result.detail)
            setIsLoading(false)
            return
          }

          // Did we get a token?
          if (!result.token) {
            throw new Error(
              `No token returned. Auto login failed with result: ${JSON.stringify(
                result
              )}`
            )
          }

          // Success!
          const redirectURL =
            result.is_new_user === false ? ROUTE_TODAY : ROUTE_TOS_ACCEPTANCE
          handleAuthSuccess(result, redirectURL)
        })
        .catch(error => {
          console.error('error', error)
          Sentry.captureException(error)
          setSignupError(error.message)
          setIsLoading(false)
        })
    } catch (error) {
      Sentry.captureException(error)
      setSignupError(error.message)
      setIsLoading(false)
    }
  }, [state, code, provider, handleAuthSuccess])

  return (
    <Loading isLoaded={isLoading}>
      <Container>
        {/* Did we receive a signup error? */}
        {signupError && (
          <>
            <div className='error'>
              <i className='icon bi-exclamation-circle'></i>{' '}
              <strong>Error: </strong>
              {/* Do we have an array of messages or a string? */}
              {!Array.isArray(signupError) ? (
                String(signupError) // Force to string
              ) : (
                <>
                  {signupError.map((error, i) => (
                    <span key={i}>{error.msg} </span>
                  ))}
                </>
              )}
            </div>
            <Row>
              <Col>
                <p className='muted my-5 text-center'>
                  <a href={ROUTE_LOGIN}>
                    <Button variant='secondary'>return to login</Button>
                  </a>
                </p>
              </Col>
            </Row>
          </>
        )}
      </Container>
    </Loading>
  )
}
